import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import ArtworkDataContext from '@/context/ArtworkDataContext';
import useGalleryPurchaseRejection from '@/hooks/useGalleryPurchaseRejection';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import SoldPriceValue from '@/pages/artwork/components/SoldPriceValue';
import useAuctionLiveStream, {
  buildBidSignature,
  deriveBidIdentifier,
  normalizeAuctionBid,
} from '@/pages/artwork/hooks/useAuctionLiveStream';
import useAuctionPaymentMethods from '@/pages/artwork/hooks/useAuctionPaymentMethods';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import auctionService from '@/services/auction.service';
import UserService from '@/services/user.service';
import SellInStorePopup from '@/shared-components/artwork/sell-in-store/SellInStorePopup';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';
import NumberUtils from '@/utils/number.utils';

import AddToArtlist from '../artwork-header-mobile/options/add-to-artlist';
import { ArtworkOfferPrice } from '../components/ArtworkOfferElements';
import {
  clearArtworkPurchaseResume,
  getArtworkPurchaseResume,
} from '../utils/artworkPurchaseResume.utils';
import {
  getArtworkLimitedEditionOptions,
  getArtworkSaleSummary,
  getArtworkShippingConfig,
  getArtworkViewerContext,
} from '../utils/artworkView.selectors';
import { parseOfferAmount, resolveActiveViewerOffer } from '../utils/offer.utils';

import ArtworkAuctionDesktop from './components/ArtworkAuctionDesktop';
import ArtworkMoreDetailsDesktop from './components/ArtworkMoreDetailsDesktop';
import { buildOwnershipSectionData } from './components/artworkMoreDetailsDesktop.helpers';
import BuyDetailsDesktop from './components/BuyDetailsDesktop';
import {
  buildResellEligibilityPayload,
  mapResellStatusToOption,
} from './components/purchaseFooter.constants';
import PurchaseFooterDesktop from './components/PurchaseFooterDesktop';
import { resolveLimitedEditionContext } from './utils/limitedEdition.utils';
import Socials from './socials';

import './index.css';

function requiresPaymentAuthorization(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const status = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : '';
  return payload.requires_payment_method === true
    || payload.requires_action === true
    || status === 'requires_payment_method'
    || status === 'requires_action';
}

function normaliseId(candidate) {
  if (candidate === null || candidate === undefined) {
    return null;
  }
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return String(candidate);
  }
  if (typeof candidate === 'object') {
    if (candidate === null) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(candidate, '_id')) {
      return normaliseId(candidate._id);
    }
    if (Object.prototype.hasOwnProperty.call(candidate, 'id')) {
      return normaliseId(candidate.id);
    }
    if (typeof candidate.toString === 'function') {
      return candidate.toString();
    }
  }
  return null;
}

function buildPendingBidPayload({
  artworkId,
  amount,
  currency,
  idempotencyKey,
  paymentMethodId,
}) {
  const parsedAmount = Number(amount);
  if (!artworkId || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
    return null;
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10);
  const baseKey = typeof idempotencyKey === 'string' && idempotencyKey.trim()
    ? `${idempotencyKey.trim()}-profile`
    : 'bid-profile';
  const derivedKey = `${baseKey}-${Date.now()}-${randomSuffix}`.slice(0, 120);

  const payload = {
    artwork_id: artworkId,
    amount: parsedAmount,
    currency: currency ?? null,
    idempotency_key: derivedKey,
  };

  if (typeof paymentMethodId === 'string' && paymentMethodId.trim()) {
    payload.payment_method_id = paymentMethodId.trim();
  }
  return payload;
}

export default function ContentSection({ selectedSocial, setSelectedSocial }) {
  const location = useLocation();
  const artworkPurchaseResumeState = location.state?.artworkPurchaseResume ?? null;
  const tBuyDetails = useTranslations('common.shared.artwork_view.buy_details');
  const tPurchaseFooter = useTranslations('common.shared.artwork_view.purchase_footer');
  const {
    artworkData: contextArtworkData,
    socialsData,
    setSocialsData,
    onUnlockSoldPrice,
    soldPricePending,
  } = useContext(ArtworkDataContext);

  const { state } = useStateHandler();
  const paymentProfile = usePaymentProfile();
  const paymentProfileStatus = paymentProfile?.status ?? 'unknown';
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const handleAuthorizationChallenge = paymentProfile?.handleAuthorizationChallenge;
  const {
    paymentMethods,
    paymentMethodsError,
    isLoadingPaymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    loadPaymentMethods,
    refreshPaymentMethods,
    hasPaymentMethods,
  } = useAuctionPaymentMethods();
  const [artworkDataOverride, setArtworkDataOverride] = useState(null);
  const artworkIdentity = useMemo(() => (
    contextArtworkData?._id
    ?? contextArtworkData?.product_id
    ?? contextArtworkData?.slug
    ?? null
  ), [contextArtworkData]);
  const artworkData = artworkDataOverride ?? contextArtworkData;
  const routePurchaseResumeState = (
    artworkPurchaseResumeState?.artworkId
    && String(artworkPurchaseResumeState.artworkId) === String(artworkData?._id)
  ) ? artworkPurchaseResumeState : null;
  const initialPurchaseResumeState = routePurchaseResumeState ?? getArtworkPurchaseResume(artworkData?._id);

  useEffect(() => {
    if (initialPurchaseResumeState?.artworkId) {
      clearArtworkPurchaseResume(initialPurchaseResumeState.artworkId);
    }
  }, [initialPurchaseResumeState]);

  useEffect(() => {
    setArtworkDataOverride(null);
  }, [artworkIdentity]);

  const setArtworkData = useCallback((value) => {
    setArtworkDataOverride((previousOverride) => {
      const previousResolved = previousOverride ?? contextArtworkData;
      return typeof value === 'function' ? value(previousResolved) : value;
    });
  }, [contextArtworkData]);

  const [sellInStoreOpen, setSellInStoreOpen] = useState(false);
  const [openAddToArtlist, setOpenAddToArtlist] = useState(false);
  const requireAccount = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });

  // Helper to gracefully parse numeric fields coming from the API.
  const pickAmount = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
    return null;
  };

  // Show the auction UI if the artwork is marked as auction.
  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const shippingData = useMemo(() => getArtworkShippingConfig(artworkData), [artworkData]);
  const freeShippingAvailable = useMemo(() => {
    const { artwork_shipping_own: ownShipping, artwork_shipping_rest: restShipping } = shippingData;
    const validPayer = ['You', 'My gallery'];
    return validPayer.includes(ownShipping?.payer) && validPayer.includes(restShipping?.payer);
  }, [shippingData]);
  // Gate the *ability to bid* with the enabled flag.
  const isAuctionEnabled = Boolean(artworkData?.artwork_auction?.is_enabled);

  const viewerId = normaliseId(state?.user_session?._id ?? state?.user_session?.id ?? null);
  const galleryOwnerId = normaliseId(
    artworkData?.artwork_about?.artwork_gallery?._id
    ?? artworkData?.artwork_about?.artwork_gallery
    ?? null,
  );
  const artworkCreatorId = normaliseId(artworkData?.artwork_about?.artwork_artist);
  const viewerTypeRaw = state?.user_session?.user_type ?? state?.user_session?.__t ?? null;
  const viewerType = typeof viewerTypeRaw === 'string' ? viewerTypeRaw.trim().toLowerCase() : '';
  const isGalleryViewer = viewerType === 'gallery';
  const galleryRejection = useGalleryPurchaseRejection();

  const isOwnArtwork = Boolean(viewerId && galleryOwnerId && viewerId === galleryOwnerId);
  const isArtworkCreatorViewer = Boolean(viewerId && artworkCreatorId && viewerId === artworkCreatorId);
  const isAuctionBiddingBlocked = isOwnArtwork || isGalleryViewer || isArtworkCreatorViewer;

  const formattedPrice = useMemo(() => {
    const { artwork_price: price, artwork_currency: currency } = artworkData.artwork_about;
    if (price === null || price === undefined || price === '') return '—';
    return NumberUtils.getArtworkPrice(price, currency);
  }, [artworkData.artwork_about]);
  const activeOffer = useMemo(
    () => resolveActiveViewerOffer(artworkData),
    [artworkData],
  );
  const activeOfferPrice = useMemo(
    () => parseOfferAmount(activeOffer?.offer_price),
    [activeOffer?.offer_price],
  );

  const limitedEditionContext = useMemo(
    () => resolveLimitedEditionContext(artworkData),
    [artworkData],
  );
  const productMetadata = limitedEditionContext.metadata ?? {};
  const productId = artworkData?.product_id ?? null;
  const isLimitedEditionProduct = limitedEditionContext.isLimitedEdition;
  const limitedEditionNumber = limitedEditionContext.editionNumber;
  const limitedEditionSize = limitedEditionContext.editionSize;
  const limitedEditionOptions = useMemo(
    () => getArtworkLimitedEditionOptions(artworkData),
    [artworkData],
  );

  // Detect whether at least one limited edition still has units available.
  const hasLimitedEditionAvailable = useMemo(() => {
    const editions = limitedEditionOptions || {};
    return Object.values(editions).some((edition) => {
      if (!edition) {
        return false;
      }
      const { quantity, sold } = edition;
      if (typeof quantity === 'number' && typeof sold === 'number') {
        return quantity > sold;
      }
      return true;
    });
  }, [limitedEditionOptions]);

  // -------------------------
  // BLOQUE: Lógica de AUCTIONS (add/auctions)
  // -------------------------
  const auctionDetails = artworkData?.artwork_auction ?? {};
  const auctionBidsRaw = auctionDetails?.bids;

  // Detecta fin de subasta por endsAt < now (independiente del flag is_enabled)
  const auctionHasEnded = useMemo(() => {
    const ts = auctionDetails?.ends_at ? new Date(auctionDetails.ends_at).getTime() : NaN;
    return Number.isFinite(ts) && ts <= Date.now();
  }, [auctionDetails?.ends_at]);

  const auctionBids = useMemo(() => {
    if (!isAuctionArtwork) return [];
    const list = Array.isArray(auctionBidsRaw) ? auctionBidsRaw : [];
    return [...list].sort((a, b) => {
      const aDate = new Date(a?.created_at ?? a?.placed_at ?? 0).getTime();
      const bDate = new Date(b?.created_at ?? b?.placed_at ?? 0).getTime();
      return bDate - aDate;
    });
  }, [auctionBidsRaw, isAuctionArtwork]);

  const auctionCurrency = artworkData?.artwork_about?.artwork_currency
    || auctionDetails?.currency
    || 'EUR';

  const startingBidAmount = useMemo(
    () => pickAmount(
      auctionDetails?.price_starting_at,
      artworkData?.artwork_about?.price_starting_at,
      artworkData?.artwork_about?.artwork_price,
    ) ?? 0,
    [auctionDetails?.price_starting_at, artworkData?.artwork_about?.artwork_price, artworkData?.artwork_about?.price_starting_at],
  );

  const highestBidAmount = useMemo(() => {
    if (!isAuctionArtwork) return null;

    let top = null;
    auctionBids.forEach((bid) => {
      const candidate = pickAmount(bid?.amount);
      if (candidate !== null) {
        top = top === null ? candidate : Math.max(top, candidate);
      }
    });

    if (top !== null) {
      return top;
    }

    return pickAmount(
      auctionDetails?.current_bid_amount,
      auctionDetails?.current_bid?.amount,
      auctionDetails?.highest_bid_amount,
    );
  }, [auctionBids, auctionDetails?.current_bid?.amount, auctionDetails?.current_bid_amount, auctionDetails?.highest_bid_amount, isAuctionArtwork]);

  const auctionFooterDefaults = useMemo(() => {
    if (!isAuctionArtwork) return null;

    // Mirror live auction conventions: headline for the current bid plus a quick snapshot of activity.
    const hasBids = auctionBids.length > 0 && highestBidAmount !== null;
    const displayAmountCandidate = hasBids
      ? (highestBidAmount ?? startingBidAmount)
      : startingBidAmount;
    const safeAmount = Number.isFinite(displayAmountCandidate)
      ? Math.max(0, Math.round(displayAmountCandidate))
      : 0;
    const amountValue = NumberUtils.getArtworkPrice(safeAmount, auctionCurrency);
    const bidsLabel = hasBids
      ? `${auctionBids.length} bid${auctionBids.length === 1 ? '' : 's'}`
      : 'No bids yet';
    const visitorAuctionCta = auctionHasEnded ? 'See winner' : 'See auction';
    const buttonLabel = isOwnArtwork ? 'See Auction' : visitorAuctionCta;

    // Textos cuando la subasta ha finalizado
    if (auctionHasEnded) {
      return {
        amountLabel: '',
        amountValue,
        buttonLabel,
        disabled: false,
        loading: false,
        auxiliaryContent: (
          <span className="purchase-footer__desktop__aux-text">{bidsLabel}</span>
        ),
        label: hasBids ? 'Winning bid' : 'Starting bid',
        variant: 'auction',
      };
    }

    return {
      amountLabel: '',
      amountValue,
      // Internal gallery view should surface the public auction layout but keep actions read-only.
      buttonLabel,
      disabled: false,
      loading: false,
      auxiliaryContent: (
        <span className="purchase-footer__desktop__aux-text">{bidsLabel}</span>
      ),
      label: hasBids ? 'Current bid' : 'Starting bid',
      variant: 'auction',
    };
  }, [auctionBids, auctionCurrency, highestBidAmount, isAuctionArtwork, isOwnArtwork, startingBidAmount, auctionHasEnded]);

  // -------------------------
  // BLOQUE: Lógica de REVENTA / EDICIONES (main-evil)
  // -------------------------
  const ownershipInfo = useMemo(
    () => buildOwnershipSectionData(artworkData, state.user_session, getArtworkViewerContext(artworkData)),
    [
      artworkData?.artwork_about,
      artworkData?.artwork_status,
      artworkData?.artwork_flags,
      artworkData?.ownership_context,
      artworkData?.ownership_summary,
      artworkData?.viewer_context,
      state.user_session,
    ],
  );

  const isLimitedEditionSold = ownershipInfo.isSold && isLimitedEditionProduct;
  const limitedEditionIsPrivate = productMetadata?.is_private === true;

  const resaleEligibility = useMemo(
    () => (productMetadata?.resale_elegible ?? null),
    [productMetadata?.resale_elegible],
  );

  const normalizedResaleOption = useMemo(
    () => mapResellStatusToOption(resaleEligibility),
    [resaleEligibility],
  );

  const [resaleOption, setResaleOption] = useState(normalizedResaleOption);
  const [resaleSaving, setResaleSaving] = useState(false);

  useEffect(() => {
    setResaleOption(normalizedResaleOption);
  }, [normalizedResaleOption]);

  const saleSummary = useMemo(() => getArtworkSaleSummary(artworkData), [artworkData]);
  const lastSalePriceRaw = saleSummary?.price ?? artworkData?.artwork_about?.artwork_price ?? null;

  const lastSalePriceNumeric = useMemo(() => {
    if (lastSalePriceRaw === null || lastSalePriceRaw === undefined || lastSalePriceRaw === '') {
      return null;
    }
    const numeric = Number(lastSalePriceRaw);
    return Number.isFinite(numeric) ? numeric : null;
  }, [lastSalePriceRaw]);

  const lastSaleCurrency = useMemo(() => {
    return saleSummary?.currency ?? artworkData?.artwork_about?.artwork_currency ?? '';
  }, [
    artworkData?.artwork_about?.artwork_currency,
    saleSummary?.currency,
  ]);

  const lastSalePriceFormatted = useMemo(() => {
    if (!ownershipInfo.isOriginalProduct || !ownershipInfo.isSold) {
      return formattedPrice;
    }
    if (lastSalePriceRaw === null || lastSalePriceRaw === undefined || lastSalePriceRaw === '') {
      return '—';
    }
    return NumberUtils.getArtworkPrice(lastSalePriceRaw, lastSaleCurrency);
  }, [formattedPrice, ownershipInfo.isOriginalProduct, ownershipInfo.isSold, lastSaleCurrency, lastSalePriceRaw]);

  // -------------------------
  // Footer state por defecto (combina Auctions + Reventa)
  // -------------------------
  const resaleFeaturesEnabled = false;

  const defaultFooterState = useMemo(() => {
    // 1) Subastas: si la obra es de subasta, usamos defaults de subasta
    if (isAuctionArtwork && auctionFooterDefaults) {
      const ownerShouldSeeResale = ownershipInfo.isOwnerView && ownershipInfo.isSold;
      if (!ownerShouldSeeResale) {
        if (ownershipInfo.isOriginalProduct && ownershipInfo.isSold && artworkData?.sold_price_access?.status !== 'unlocked') {
          return {
            ...auctionFooterDefaults,
            amountLabel: 'Sold',
            amountValue: (
              <SoldPriceValue
                artworkData={artworkData}
                value="—"
                onUnlock={onUnlockSoldPrice}
                pending={soldPricePending}
              />
            ),
            label: null,
          };
        }
        return auctionFooterDefaults;
      }
    }

    // 2) Reventa/Ediciones (obra vendida)
    if (ownershipInfo.isSold && (ownershipInfo.isOriginalProduct || isLimitedEditionProduct)) {
      return {
        amountLabel: 'Sold',
        amountValue: ownershipInfo.isOriginalProduct ? (
          <SoldPriceValue
            artworkData={artworkData}
            value={lastSalePriceFormatted ?? '—'}
            onUnlock={onUnlockSoldPrice}
            pending={soldPricePending}
          />
        ) : (lastSalePriceFormatted ?? '—'),
        buttonLabel: '',
        disabled: false,
        loading: false,
        auxiliaryContent: null,
        label: null,
        variant: 'default',
      };
    }

    // 3) Caso normal (compra estándar)
    return {
      amountLabel: tPurchaseFooter(ownershipInfo.isOriginalProduct ? 'original_label' : 'total_label'),
      amountValue: activeOfferPrice !== null ? (
        <ArtworkOfferPrice
          originalPrice={artworkData?.artwork_about?.artwork_price}
          offerPrice={activeOfferPrice}
          currency={activeOffer?.currency ?? artworkData?.artwork_about?.artwork_currency}
        />
      ) : (
        formattedPrice
      ),
      buttonLabel: isOwnArtwork
        ? 'Sell In-store'
        : tPurchaseFooter('desktop_button_label'),
      disabled: false,
      loading: false,
      auxiliaryContent: null,
      offerPriceDetails: null,
      label: null,
      variant: 'default',
    };
  }, [
    activeOffer?.currency,
    activeOfferPrice,
    auctionFooterDefaults,
    artworkData?.artwork_about?.artwork_currency,
    artworkData?.artwork_about?.artwork_price,
    artworkData?.sold_price_access?.status,
    formattedPrice,
    isAuctionArtwork,
    isLimitedEditionProduct,
    isOwnArtwork,
    lastSalePriceFormatted,
    onUnlockSoldPrice,
    soldPricePending,
    ownershipInfo.isOwnerView,
    ownershipInfo.isOriginalProduct,
    ownershipInfo.isSold,
    tPurchaseFooter,
  ]);

  const [currentView, setCurrentView] = useState(initialPurchaseResumeState ? 'purchase' : 'details');
  const [purchaseType, setPurchaseType] = useState(initialPurchaseResumeState?.purchaseType ?? 'standard');
  const [footerState, setFooterState] = useState(defaultFooterState);
  const [initialLimitedEditionSelection, setInitialLimitedEditionSelection] = useState(null);
  const checkoutHandlerRef = useRef(null);
  const contentBodyRef = useRef(null);

  useEffect(() => {
    if (currentView === 'details') {
      setFooterState(defaultFooterState);
      checkoutHandlerRef.current = null;
    }
  }, [currentView, defaultFooterState]);

  useEffect(() => {
    if (currentView === 'auction' && state?.user_session) {
      loadPaymentMethods().catch(() => {});
    }
  }, [currentView, state?.user_session, loadPaymentMethods]);
  useEffect(() => {
    if (currentView !== 'purchase') {
      setInitialLimitedEditionSelection(null);
    }
  }, [currentView]);

  const handleFooterChange = useCallback((nextState) => {
    setFooterState((prev) => ({
      ...prev,
      ...nextState,
    }));
  }, []);

  const registerCheckoutHandler = useCallback((handler) => {
    checkoutHandlerRef.current = handler;
  }, []);

  const handleManagePaymentMethods = useCallback(async () => {
    if (typeof ensurePaymentProfile !== 'function') {
      return;
    }
    try {
      await ensurePaymentProfile({ forceSetup: true });
      await refreshPaymentMethods();
    }
    catch (error) {
      if (error?.code === 'BIDDER_PROFILE_CANCELLED') {
        return;
      }
      console.warn('[ARTWORK_AUCTION] Unable to open payment method manager.', error);
    }
  }, [ensurePaymentProfile, refreshPaymentMethods]);

  // Centralised entry point for both standard and limited edition flows.
  const startPurchaseFlow = useCallback((type) => {
    setPurchaseType(type);
    setFooterState((prev) => ({
      ...prev,
      buttonLabel: tBuyDetails('footer.pay_now'),
      disabled: true,
      offerPriceDetails: null,
      auxiliaryContent: (
        <span className="purchase-footer__desktop__aux-text">{tBuyDetails('footer.shipping_prefix')}: -</span>
      ),
      amountValue: '-',
    }));
    setCurrentView('purchase');
  }, [tBuyDetails]);

  const handleResaleOptionUpdate = useCallback(async (nextOption) => {
    if (resaleSaving) {
      return;
    }

    if (nextOption === resaleOption) {
      return;
    }

    if (!ownershipInfo.isSold || !ownershipInfo.isOwnerView) {
      setResaleOption(nextOption);
      return;
    }

    setResaleSaving(true);
    try {
      if (artworkData?.product_id) {
        const payload = {
          product_id: artworkData.product_id,
          product_metadata: {
            ...productMetadata,
            resale_elegible: buildResellEligibilityPayload(nextOption),
          },
        };

        const { response } = await UserService.editProduct(payload);
        if (!response?.ok) {
          throw new Error('Failed to update resale option');
        }
      }

      setResaleOption(nextOption);
    } finally {
      setResaleSaving(false);
    }
  }, [artworkData?.product_id, ownershipInfo.isSold, ownershipInfo.isOwnerView, productMetadata, resaleOption, resaleSaving]);

  const handleFooterButtonClick = () => {
    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.PURCHASE_INTENT,
        metadata: { source: 'purchase-footer__desktop__button' },
      });
    }

    if (isAuctionArtwork) {
      setCurrentView('auction');
      return;
    }

    if (currentView === 'purchase' && purchaseType === 'limited_edition') {
      checkoutHandlerRef.current?.();
      return;
    }

    if (currentView === 'details' && galleryRejection.isGalleryAccount && !isOwnArtwork) {
      galleryRejection.show();
      return;
    }

    if (isOwnArtwork) {
      setSellInStoreOpen(true);
      return;
    }

    if (currentView === 'details') {
      startPurchaseFlow('standard');
      return;
    }

    checkoutHandlerRef.current?.();
  };

  const handleOpenLimitedEdition = useCallback((selection = null) => {
    setInitialLimitedEditionSelection(selection);
    startPurchaseFlow('limited_edition');
  }, [setInitialLimitedEditionSelection, startPurchaseFlow]);

  const handleOpenAddToArtlist = useCallback(() => {
    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.ADD_TO_ARTLIST,
        metadata: { source: 'add-to-artlist-icon__outline' },
      });
    }
    requireAccount(() => setOpenAddToArtlist(true));
  }, [artworkData?.slug, requireAccount]);

  const handleCloseAddToArtlist = useCallback(() => {
    setOpenAddToArtlist(false);
  }, []);

  const handleBackToDetails = useCallback(() => {
    setCurrentView('details');
    setPurchaseType('standard');
  }, []);

  const handleLikeUpdate = useCallback((likeStatus) => {
    if (!socialsData || !setSocialsData) {
      return;
    }
    setSocialsData({
      ...socialsData,
      likes_count: likeStatus
        ? socialsData.likes_count + 1
        : Math.max(socialsData.likes_count - 1, 0),
    });
  }, [setSocialsData, socialsData]);

  const handleOpenThoughts = useCallback(() => {
    setSelectedSocial?.('thoughts');
  }, [setSelectedSocial]);

  const handleOpenRatings = useCallback(() => {
    setSelectedSocial?.('ratings');
  }, [setSelectedSocial]);

  const handleOpenAskAi = useCallback(() => {
    setSelectedSocial?.('ask-ai');
  }, [setSelectedSocial]);

  const handleCloseSocials = useCallback(() => {
    setSelectedSocial?.(null);
  }, [setSelectedSocial]);

  useEffect(() => {
    if (currentView !== 'details' && selectedSocial) {
      setSelectedSocial?.(null);
    }
  }, [currentView, selectedSocial, setSelectedSocial]);

  const pendingBidRef = useRef(null);
  // Preserve the last bid metadata so we can enrich stream updates (avatars, types, etc.).
  const recentBidRef = useRef(null);
  // Cache bidder details keyed by their identity so passive observers see avatars immediately.
  const bidderDetailsRef = useRef(new Map());
  const pendingTimersRef = useRef({ verify: null, cleanup: null });
  const [pendingBidState, setPendingBidState] = useState(null);

  const clearPendingTimers = useCallback(() => {
    const { verify, cleanup } = pendingTimersRef.current;
    if (verify) {
      clearTimeout(verify);
    }
    if (cleanup) {
      clearTimeout(cleanup);
    }
    pendingTimersRef.current = { verify: null, cleanup: null };
  }, []);

  const confirmPendingBid = useCallback(() => {
    clearPendingTimers();
    setPendingBidState((previous) => {
      if (previous?.status === 'confirmed') {
        return previous;
      }
      return {
        status: 'confirmed',
        message: 'Bid confirmed!',
      };
    });
    pendingTimersRef.current.cleanup = setTimeout(() => {
      setPendingBidState(null);
      pendingTimersRef.current.cleanup = null;
    }, 3000);
  }, [clearPendingTimers]);

  const markPendingBidWaiting = useCallback(() => {
    clearPendingTimers();
    setPendingBidState({
      status: 'waiting',
      message: 'Bid submitted. Waiting for live confirmation…',
    });

    pendingTimersRef.current.verify = setTimeout(() => {
      if (!pendingBidRef.current) {
        return;
      }
      setPendingBidState({
        status: 'pending_verification',
        message: 'Bid pending verification. We will update you shortly—no need to resubmit.',
      });
      pendingTimersRef.current.verify = null;
    }, 8000);
  }, [clearPendingTimers]);

  const doesCandidateMatchMeta = useCallback((candidateBid, meta) => {
    if (!candidateBid || !meta) {
      return false;
    }

    const candidateAmount = Number(candidateBid?.amount);
    const metaAmount = Number(meta?.amount);
    if (Number.isFinite(metaAmount)) {
      if (!Number.isFinite(candidateAmount) || Number(candidateAmount) !== metaAmount) {
        return false;
      }
    } else if (!Number.isFinite(candidateAmount)) {
      return false;
    }

    const candidateBidderId =
      candidateBid?.bidder?._id ??
      candidateBid?.bidder_id ??
      candidateBid?.bidderId ??
      candidateBid?.bidder?.id ??
      candidateBid?.bidder?.user_id ??
      null;
    const candidateUsername =
      candidateBid?.bidder_username ??
      candidateBid?.bidderUsername ??
      candidateBid?.bidder?.username ??
      candidateBid?.bidder?.user_username ??
      null;
    const candidateIdempotencyKey =
      candidateBid?.idempotency_key ??
      candidateBid?.idempotencyKey ??
      candidateBid?.bid?.idempotency_key ??
      candidateBid?.bid?.idempotencyKey ??
      null;

    if (meta.idempotencyKey && candidateIdempotencyKey && meta.idempotencyKey === candidateIdempotencyKey) {
      return true;
    }

    const matchesId =
      meta.bidderId &&
      candidateBidderId &&
      meta.bidderId === candidateBidderId;
    const matchesUsername =
      meta.bidderUsername &&
      candidateUsername &&
      meta.bidderUsername === candidateUsername;

    if (!matchesId && !matchesUsername) {
      const hasIdentity = Boolean(candidateBidderId || candidateUsername || candidateIdempotencyKey);
      if (hasIdentity) {
        return false;
      }
    }

    return true;
  }, []);

  const extractBidMeta = useCallback((bid) => {
    if (!bid) {
      return null;
    }
    const candidate = bid.bid ?? bid;
    const amount = Number(candidate?.amount);

    const bidder = candidate?.bidder && typeof candidate.bidder === 'object' ? candidate.bidder : null;
    const bidderId =
      candidate?.bidderId ??
      candidate?.bidder_id ??
      bidder?._id ??
      bidder?.id ??
      bidder?.user_id ??
      null;
    const bidderUsername =
      candidate?.bidderUsername ??
      candidate?.bidder_username ??
      bidder?.user_username ??
      bidder?.username ??
      bidder?.bidder_username ??
      null;
    const bidderType =
      candidate?.bidderType ??
      candidate?.bidder_type ??
      bidder?.user_type ??
      bidder?.type ??
      bidder?.bidder_type ??
      null;
    const bidderImageId =
      candidate?.bidderImageId ??
      candidate?.bidder_image_id ??
      bidder?.user_image?.image_id ??
      bidder?.gallery_image?.image_id ??
      bidder?.artist_image?.image_id ??
      bidder?.image_id ??
      null;
    const idempotencyKey =
      candidate?.idempotencyKey ??
      candidate?.idempotency_key ??
      null;

    return {
      amount: Number.isFinite(amount) ? amount : null,
      bidderId: bidderId || null,
      bidderUsername: bidderUsername || null,
      bidderType: bidderType || null,
      bidderImageId: bidderImageId || null,
      idempotencyKey: idempotencyKey || null,
    };
  }, []);

  const getBidderCacheKey = useCallback((meta) => {
    if (!meta) {
      return null;
    }
    if (meta.bidderId) {
      return `id:${meta.bidderId}`;
    }
    if (meta.bidderUsername) {
      return `username:${meta.bidderUsername}`;
    }
    return null;
  }, []);

  const registerBidderDetails = useCallback((meta) => {
    if (!meta) {
      return;
    }
    const key = getBidderCacheKey(meta);
    if (!key) {
      return;
    }

    const previous = bidderDetailsRef.current.get(key) || {};
    const merged = { ...previous };
    let mutated = false;

    const assignIfPresent = (field) => {
      if (meta[field] !== undefined && meta[field] !== null && merged[field] !== meta[field]) {
        merged[field] = meta[field];
        mutated = true;
      }
    };

    assignIfPresent('amount');
    assignIfPresent('bidderId');
    assignIfPresent('bidderUsername');
    assignIfPresent('bidderType');
    assignIfPresent('bidderImageId');
    assignIfPresent('idempotencyKey');

    if (mutated) {
      bidderDetailsRef.current.set(key, merged);
    }
  }, [getBidderCacheKey]);

  const registerBidderSummaries = useCallback((summary) => {
    if (!summary) {
      return;
    }

    const items = Array.isArray(summary)
      ? summary
      : (typeof summary === 'object' ? Object.values(summary) : []);
    items.forEach((entry) => {
      if (!entry) {
        return;
      }

      const meta = extractBidMeta({
        amount:
          entry?.amount ??
          entry?.latest_bid_amount ??
          entry?.highest_bid_amount ??
          null,
        bidder_id:
          entry?.bidder_id ??
          entry?.bidderId ??
          entry?._id ??
          entry?.id ??
          null,
        bidder_username:
          entry?.bidder_username ??
          entry?.bidderUsername ??
          entry?.username ??
          entry?.user_username ??
          null,
        bidder_type:
          entry?.bidder_type ??
          entry?.bidderType ??
          entry?.user_type ??
          entry?.type ??
          null,
        bidder_image_id:
          entry?.bidder_image_id ??
          entry?.image_id ??
          entry?.image ??
          null,
        bidder: {
          user_image: entry?.image_id || entry?.bidder_image_id
            ? { image_id: entry?.image_id ?? entry?.bidder_image_id ?? entry?.image ?? null }
            : undefined,
        },
      });

      registerBidderDetails(meta);
    });
  }, [extractBidMeta, registerBidderDetails]);

  const applyMetaToBid = useCallback((bid, meta) => {
    if (!bid || !meta) {
      return bid;
    }

    let mutated = false;
    const nextBid = { ...bid };

    if (meta.bidderId && nextBid.bidder_id !== meta.bidderId) {
      nextBid.bidder_id = meta.bidderId;
      mutated = true;
    }
    if (meta.bidderUsername && nextBid.bidder_username !== meta.bidderUsername) {
      nextBid.bidder_username = meta.bidderUsername;
      mutated = true;
    }
    if (meta.bidderType && nextBid.bidder_type !== meta.bidderType) {
      nextBid.bidder_type = meta.bidderType;
      mutated = true;
    }
    if (meta.bidderImageId && nextBid.bidder_image_id !== meta.bidderImageId) {
      nextBid.bidder_image_id = meta.bidderImageId;
      mutated = true;
    }

    let bidderMutated = false;
    let nextBidder = nextBid.bidder ? { ...nextBid.bidder } : null;
    const ensureBidder = () => {
      if (!nextBidder) {
        nextBidder = {};
      }
    };
    const assignBidderField = (key, value) => {
      if (value === undefined || value === null) {
        return;
      }
      ensureBidder();
      if (nextBidder[key] === value) {
        return;
      }
      nextBidder[key] = value;
      bidderMutated = true;
    };

    assignBidderField('_id', meta.bidderId);
    assignBidderField('id', meta.bidderId);
    assignBidderField('user_id', meta.bidderId);
    assignBidderField('user_username', meta.bidderUsername);
    assignBidderField('username', meta.bidderUsername);
    assignBidderField('bidder_username', meta.bidderUsername);
    assignBidderField('user_type', meta.bidderType);
    assignBidderField('bidder_type', meta.bidderType);

    if (meta.bidderImageId) {
      ensureBidder();
      const currentImage = nextBidder.user_image ? { ...nextBidder.user_image } : {};
      if (currentImage.image_id !== meta.bidderImageId) {
        currentImage.image_id = meta.bidderImageId;
        bidderMutated = true;
      }
      nextBidder.user_image = currentImage;
    }

    if (bidderMutated) {
      nextBid.bidder = nextBidder;
      mutated = true;
    }

    return mutated ? nextBid : bid;
  }, []);

  const enhanceBidWithFallbacks = useCallback((candidate, additionalSources = []) => {
    if (!candidate) {
      return candidate;
    }

    let result = candidate;
    const candidateMeta = extractBidMeta(candidate);
    const cacheKey = getBidderCacheKey(candidateMeta);
    const cachedMeta = cacheKey ? bidderDetailsRef.current.get(cacheKey) : null;

    const metaSources = [
      extractBidMeta(pendingBidRef.current),
      extractBidMeta(recentBidRef.current),
      cachedMeta,
      ...additionalSources.map(extractBidMeta),
    ].filter(Boolean);

    metaSources.forEach((meta) => {
      if (!meta) {
        return;
      }
      if (!doesCandidateMatchMeta(result, meta)) {
        return;
      }
      const enriched = applyMetaToBid(result, meta);
      if (enriched !== result) {
        result = enriched;
      }
    });

    registerBidderDetails(extractBidMeta(result));

    return result;
  }, [applyMetaToBid, doesCandidateMatchMeta, extractBidMeta, getBidderCacheKey, registerBidderDetails]);

  const resolvePendingBidMatch = useCallback((candidateBid) => {
    if (!candidateBid) {
      return false;
    }

    const sources = [
      { ref: pendingBidRef, meta: pendingBidRef.current },
      { ref: recentBidRef, meta: recentBidRef.current },
    ];

    let matchedMeta = null;
    sources.some((source) => {
      if (!source.meta) {
        return false;
      }
      if (!doesCandidateMatchMeta(candidateBid, source.meta)) {
        return false;
      }
      matchedMeta = source.meta;
      return true;
    });

    if (!matchedMeta) {
      return false;
    }

    confirmPendingBid();

    [pendingBidRef, recentBidRef].forEach((ref) => {
      const current = ref.current;
      if (!current) {
        return;
      }

      if (current === matchedMeta) {
        ref.current = null;
        return;
      }

      if (
        matchedMeta.idempotencyKey &&
        current.idempotencyKey &&
        matchedMeta.idempotencyKey === current.idempotencyKey
      ) {
        ref.current = null;
        return;
      }

      const currentAmount = Number(current?.amount);
      const matchedAmount = Number(matchedMeta?.amount);
      if (!Number.isFinite(currentAmount) || currentAmount !== matchedAmount) {
        return;
      }

      const currentBidderId = current?.bidderId ?? null;
      const matchedBidderId = matchedMeta?.bidderId ?? null;
      const currentBidderUsername = current?.bidderUsername ?? null;
      const matchedBidderUsername = matchedMeta?.bidderUsername ?? null;

      const sameId = currentBidderId && matchedBidderId && currentBidderId === matchedBidderId;
      const sameUsername =
        currentBidderUsername &&
        matchedBidderUsername &&
        currentBidderUsername === matchedBidderUsername;

      if (sameId || sameUsername || (!currentBidderId && !currentBidderUsername)) {
        ref.current = null;
      }
    });

    return true;
  }, [confirmPendingBid, doesCandidateMatchMeta]);

  useEffect(() => () => {
    clearPendingTimers();
    pendingBidRef.current = null;
    recentBidRef.current = null;
    bidderDetailsRef.current.clear();
  }, [clearPendingTimers]);

  useEffect(() => {
    if (currentView !== 'auction') {
      pendingBidRef.current = null;
      recentBidRef.current = null;
      bidderDetailsRef.current.clear();
      clearPendingTimers();
      setPendingBidState(null);
    }
  }, [clearPendingTimers, currentView]);

  useEffect(() => {
    const auction = artworkData?.artwork_auction ?? null;
    if (!auction) {
      return;
    }

    if (Array.isArray(auction.bids)) {
      auction.bids
        .map(extractBidMeta)
        .forEach(registerBidderDetails);
    }
    if (auction.current_bid) {
      registerBidderDetails(extractBidMeta(auction.current_bid));
    }
    if (auction.bidder_summary) {
      registerBidderSummaries(auction.bidder_summary);
    }
  }, [artworkData?.artwork_auction, extractBidMeta, registerBidderDetails, registerBidderSummaries]);

  const applyAuctionUpdate = useCallback(({
    bid = null,
    auctionPatch = null,
    aboutPatch = null,
    replaceBids = undefined,
  } = {}) => {
    const candidateBids = [];
    const candidateIds = new Set();
    const registerCandidate = (candidate) => {
      if (!candidate) {
        return;
      }
      registerBidderDetails(extractBidMeta(candidate));
      const identifier = deriveBidIdentifier(candidate);
      if (identifier) {
        if (candidateIds.has(identifier)) {
          return;
        }
        candidateIds.add(identifier);
      }
      candidateBids.push(candidate);
    };

    setArtworkData((previous) => {
      if (!previous) {
        return previous;
      }

      const previousAuction = previous.artwork_auction ?? {};
      const previousAbout = previous.artwork_about ?? {};

      const additionalSources = [];
      if (Array.isArray(previousAuction.bids)) {
        additionalSources.push(...previousAuction.bids);
      }
      if (previousAuction.current_bid) {
        additionalSources.push(previousAuction.current_bid);
      }
      if (previousAuction.bidder_summary) {
        registerBidderSummaries(previousAuction.bidder_summary);
      }

      let auctionChanged = false;
      let aboutChanged = false;
      let nextAuction = previousAuction;
      let nextAbout = previousAbout;

      const ensureAuctionMutable = () => {
        if (!auctionChanged) {
          auctionChanged = true;
          nextAuction = { ...previousAuction };
        }
      };

      const ensureAboutMutable = () => {
        if (!aboutChanged) {
          aboutChanged = true;
          nextAbout = { ...previousAbout };
        }
      };

      const readFromSource = (source, keys) => {
        if (!source) return undefined;
        for (const key of keys) {
          if (source[key] !== undefined && source[key] !== null) {
            return source[key];
          }
        }
        return undefined;
      };

      if (Array.isArray(replaceBids)) {
        const normalized = replaceBids
          .map((raw) => enhanceBidWithFallbacks(normalizeAuctionBid(raw), additionalSources))
          .filter(Boolean)
          .sort((a, b) => {
            const aDate = new Date(a?.created_at ?? a?.placed_at ?? 0).getTime();
            const bDate = new Date(b?.created_at ?? b?.placed_at ?? 0).getTime();
            return bDate - aDate;
          });
        const prevSignature = buildBidSignature(previousAuction.bids);
        const nextSignature = buildBidSignature(normalized);
        if (prevSignature !== nextSignature) {
          ensureAuctionMutable();
          nextAuction.bids = normalized;
        }
        normalized.forEach(registerCandidate);
      }

      if (bid) {
        const normalizedBid = enhanceBidWithFallbacks(normalizeAuctionBid(bid), additionalSources);
        if (normalizedBid) {
          registerCandidate(normalizedBid);
          const existingBids = Array.isArray(previousAuction.bids) ? previousAuction.bids : [];
          const filtered = normalizedBid.id
            ? existingBids.filter((existing) => deriveBidIdentifier(existing) !== normalizedBid.id)
            : existingBids.filter((existing) => {
              const sameAmount = Number(existing?.amount) === Number(normalizedBid.amount);
              const existingBidderId =
                existing?.bidder?._id ??
                existing?.bidder_id ??
                existing?.bidder?.id ??
                existing?.bidder?.user_id ??
                null;
              const existingBidderUsername =
                existing?.bidder_username ??
                existing?.bidder?.username ??
                null;
              const normalizedBidderId =
                normalizedBid?.bidder?._id ??
                normalizedBid?.bidder_id ??
                normalizedBid?.bidder?.id ??
                normalizedBid?.bidder?.user_id ??
                null;
              const normalizedBidderUsername =
                normalizedBid?.bidder_username ??
                normalizedBid?.bidder?.username ??
                null;

              const sameBidder =
                (existingBidderId && normalizedBidderId && existingBidderId === normalizedBidderId) ||
                (existingBidderUsername && normalizedBidderUsername && existingBidderUsername === normalizedBidderUsername);

              return !(sameAmount && sameBidder);
            });
          const nextBids = [normalizedBid, ...filtered].sort((a, b) => {
            const aDate = new Date(a?.created_at ?? a?.placed_at ?? 0).getTime();
            const bDate = new Date(b?.created_at ?? b?.placed_at ?? 0).getTime();
            return bDate - aDate;
          });
          const prevSignature = buildBidSignature(previousAuction.bids);
          const nextSignature = buildBidSignature(nextBids);
          if (prevSignature !== nextSignature) {
            ensureAuctionMutable();
            nextAuction.bids = nextBids;
          }
        }
      }

      if (auctionPatch) {
        const map = [
          ['current_bid_price', ['current_bid_price', 'currentBidPrice']],
          ['current_bid_amount', ['current_bid_amount', 'currentBidAmount']],
          ['highest_bid_amount', ['highest_bid_amount', 'highestBidAmount']],
          ['total_bids', ['total_bids', 'totalBids']],
          ['bids_total', ['bids_total', 'bidsTotal']],
          ['ends_at', ['ends_at', 'endsAt']],
          ['state', ['state', 'status']],
          ['is_enabled', ['is_enabled', 'isEnabled']],
          ['last_bid_id', ['last_bid_id', 'lastBidId']],
          ['bidder_summary', ['bidder_summary', 'bidderSummary', 'current_bidder_summary', 'currentBidderSummary']],
        ];

        map.forEach(([targetKey, keys]) => {
          const value = readFromSource(auctionPatch, keys);
          if (value !== undefined && value !== nextAuction[targetKey]) {
            ensureAuctionMutable();
            nextAuction[targetKey] = value;
          }
        });

        const currentBidPayload = readFromSource(auctionPatch, ['current_bid', 'currentBid']);
        if (currentBidPayload) {
          const normalizedCurrentBid = enhanceBidWithFallbacks(normalizeAuctionBid(currentBidPayload), additionalSources);
          if (normalizedCurrentBid) {
            registerCandidate(normalizedCurrentBid);
            const previousCurrentBid = previousAuction.current_bid ?? null;
            const prevCurrentBidId = deriveBidIdentifier(previousCurrentBid);
            const nextCurrentBidId = deriveBidIdentifier(normalizedCurrentBid);
            const shouldUpdate =
              prevCurrentBidId !== nextCurrentBidId ||
              Number(previousCurrentBid?.amount) !== Number(normalizedCurrentBid.amount);
            if (shouldUpdate) {
              ensureAuctionMutable();
              nextAuction.current_bid = normalizedCurrentBid;
            }
          }
        }

        const highestBidPayload = readFromSource(auctionPatch, ['highest_bid', 'highestBid']);
        if (highestBidPayload) {
          const normalizedHighestBid = enhanceBidWithFallbacks(normalizeAuctionBid(highestBidPayload), additionalSources);
          if (normalizedHighestBid) {
            registerCandidate(normalizedHighestBid);
            const previousHighest = previousAuction.highest_bid ?? null;
            const prevHighestId = deriveBidIdentifier(previousHighest);
            const nextHighestId = deriveBidIdentifier(normalizedHighestBid);
            const shouldUpdateHighest =
              prevHighestId !== nextHighestId ||
              Number(previousHighest?.amount) !== Number(normalizedHighestBid.amount);
            if (shouldUpdateHighest) {
              ensureAuctionMutable();
              nextAuction.highest_bid = normalizedHighestBid;
            }
          }
        }

        if (Array.isArray(auctionPatch.bids) && replaceBids === undefined) {
          const normalized = auctionPatch.bids
            .map((raw) => enhanceBidWithFallbacks(normalizeAuctionBid(raw), additionalSources))
            .filter(Boolean)
            .sort((a, b) => {
              const aDate = new Date(a?.created_at ?? a?.placed_at ?? 0).getTime();
              const bDate = new Date(b?.created_at ?? b?.placed_at ?? 0).getTime();
              return bDate - aDate;
            });
          const prevSignature = buildBidSignature(previousAuction.bids);
          const nextSignature = buildBidSignature(normalized);
          if (prevSignature !== nextSignature) {
            ensureAuctionMutable();
            nextAuction.bids = normalized;
          }
          normalized.forEach(registerCandidate);
        }

        const closingFlag = readFromSource(auctionPatch, ['is_closed', 'closed']);
        if (closingFlag) {
          ensureAuctionMutable();
          nextAuction.is_enabled = false;
          nextAuction.state = readFromSource(auctionPatch, ['state', 'status']) || 'closed';
        }

        const bidderSummaryPatch = readFromSource(auctionPatch, ['bidder_summary', 'bidderSummary', 'current_bidder_summary', 'currentBidderSummary']);
        if (bidderSummaryPatch) {
          registerBidderSummaries(bidderSummaryPatch);
        }
      }

      if (aboutPatch) {
        const aboutMap = [
          ['final_bid_price', ['final_bid_price', 'finalBidPrice']],
        ];

        aboutMap.forEach(([targetKey, keys]) => {
          const value = readFromSource(aboutPatch, keys);
          if (value !== undefined && value !== nextAbout[targetKey]) {
            ensureAboutMutable();
            nextAbout[targetKey] = value;
          }
        });
      }

      if (!auctionChanged && !aboutChanged) {
        return previous;
      }

      return {
        ...previous,
        ...(aboutChanged ? { artwork_about: nextAbout } : {}),
        ...(auctionChanged ? { artwork_auction: nextAuction } : {}),
      };
    });

    if (candidateBids.length > 0) {
      candidateBids.forEach(resolvePendingBidMatch);
    }
  }, [enhanceBidWithFallbacks, extractBidMeta, registerBidderDetails, registerBidderSummaries, resolvePendingBidMatch, setArtworkData]);

  const handleAuctionStreamEvent = useCallback((event) => {
    if (!event) {
      return;
    }
    const payload = event.payload ?? {};
    const auctionPatch = payload.auction ?? payload.auctionState ?? null;
    const aboutPatch = payload.about ?? payload.artwork_about ?? null;

    if (event.type === 'bid') {
      applyAuctionUpdate({
        bid: payload.bid ?? payload.latestBid ?? payload,
        auctionPatch,
        aboutPatch,
      });
    } else if (event.type === 'meta') {
      applyAuctionUpdate({
        auctionPatch,
        aboutPatch,
        replaceBids: Array.isArray(auctionPatch?.bids) ? auctionPatch.bids : undefined,
      });
    } else if (event.type === 'close') {
      applyAuctionUpdate({
        auctionPatch: {
          ...auctionPatch,
          is_enabled: false,
          state: (auctionPatch && (auctionPatch.state || auctionPatch.status)) || 'closed',
        },
        aboutPatch,
      });
    } else {
      applyAuctionUpdate({
        auctionPatch,
        aboutPatch,
      });
    }
  }, [applyAuctionUpdate]);

  const {
    isCountdownFrozen,
    serverTimeOffsetMs,
  } = useAuctionLiveStream({
    artworkId: artworkData?._id,
    isAuctionEnabled,
    shouldConnect: currentView === 'auction',
    onEvent: handleAuctionStreamEvent,
    userId: state?.user_session?._id,
  });

  const handleBodyWheel = useCallback((event) => {
    const node = contentBodyRef.current;
    if (!node) {
      event.stopPropagation();
      return;
    }

    const hasScrollableContent = node.scrollHeight > node.clientHeight;
    if (!hasScrollableContent) {
      event.stopPropagation();
      return;
    }

    event.stopPropagation();
  }, []);

  const handleContainerWheel = useCallback((event) => {
    event.stopPropagation();
  }, []);

  // -------------------------
  // Visibilidad del Footer (combina lógica)
  // -------------------------
  const shouldShowFooter = useMemo(() => {
    // Nunca mostrar footer en la vista de subasta
    if (currentView === 'auction') return false;

    // Siempre mostrar durante el checkout/purchase
    if (currentView === 'purchase') return true;

    // Ocultar si la obra está retirada (withdrawn) o admin-only
    if (isRestrictedArtwork(artworkData)) {
      return false;
    }

    if (ownershipInfo.isSold) {
      return true;
    }

    // Ocultar si hay edición limitada disponible (UI de compra arriba)
    if (isLimitedEditionProduct && hasLimitedEditionAvailable) {
      return false;
    }

    // Ocultar si está abierto un social panel
    if (selectedSocial) {
      return false;
    }

    // En el resto de casos, mostrar
    return true;
  }, [
    currentView,
    hasLimitedEditionAvailable,
    isLimitedEditionProduct,
    isAuctionArtwork,
    ownershipInfo.isSold,
    selectedSocial,
    artworkData,
  ]);

  const resaleContext = useMemo(() => {
    const baseContext = {
      active:
        resaleFeaturesEnabled
        && ownershipInfo.isSold
        && (ownershipInfo.isOriginalProduct || isLimitedEditionProduct),
      isOwnerView: ownershipInfo.isOwnerView,
      option: resaleOption,
      saving: resaleSaving,
      lastSalePrice: lastSalePriceNumeric,
      lastSalePriceFormatted,
      lastSaleCurrency,
      onOptionChange: handleResaleOptionUpdate,
      resaleEligibility,
      isLimitedEdition: isLimitedEditionSold,
      limitedEdition: null,
    };

    if (isLimitedEditionProduct) {
      baseContext.limitedEdition = {
        productId,
        editionNumber: limitedEditionNumber,
        editionSize: limitedEditionSize,
        isPrivate: limitedEditionIsPrivate,
      };
    }

    return baseContext;
  }, [
    handleResaleOptionUpdate,
    isLimitedEditionProduct,
    isLimitedEditionSold,
    lastSaleCurrency,
    lastSalePriceFormatted,
    lastSalePriceNumeric,
    limitedEditionIsPrivate,
    limitedEditionNumber,
    limitedEditionSize,
    ownershipInfo.isOwnerView,
    ownershipInfo.isSold,
    ownershipInfo.isOriginalProduct,
    productId,
    resaleOption,
    resaleSaving,
    resaleEligibility,
  ]);

  const isThoughtsOrRatingsOpen = [
    'thoughts',
    'initial-thoughts',
    'ratings',
    'initial-ratings',
  ].includes(selectedSocial);
  const isArtworkAiOpen = selectedSocial === 'ask-ai' || selectedSocial === 'initial-ask-ai';

  const bodyClassName = [
    'artwork-view-content-section__desktop__body',
    'remove-scrollbar',
    'artwork-slider-lock', // <- mark as no-swipe zone
    isThoughtsOrRatingsOpen ? 'artwork-view-content-section__desktop__body--socials-open' : '',
    isArtworkAiOpen ? 'artwork-view-content-section__desktop__body--ask-ai-open' : '',
    !shouldShowFooter ? 'artwork-view-content-section__desktop__body--full' : '',
  ].filter(Boolean).join(' ');

  let displayContent;

  const handlePlaceBid = useCallback(async (rawInput) => {
    const payload = (rawInput && typeof rawInput === 'object')
      ? rawInput
      : { amount: rawInput };
    const amount = payload?.amount;
    const paymentMethodOverride =
      typeof payload?.paymentMethodId === 'string' && payload.paymentMethodId.trim()
        ? payload.paymentMethodId.trim()
        : null;

    if (!artworkData?._id) {
      throw new Error('Artwork information is unavailable.');
    }

    if (isAuctionBiddingBlocked) {
      let restrictionMessage = 'You are not allowed to place bids on this auction.';
      if (isGalleryViewer) {
        restrictionMessage = 'Gallery accounts cannot place bids.';
      }
      else if (isArtworkCreatorViewer) {
        restrictionMessage = 'Artists cannot bid on their own artworks.';
      }
      else if (isOwnArtwork) {
        restrictionMessage = 'Your gallery cannot bid on this auction.';
      }
      const restrictionError = new Error(restrictionMessage);
      restrictionError.code = 'BIDDER_NOT_ELIGIBLE';
      throw restrictionError;
    }

    if (typeof ensurePaymentProfile === 'function' && paymentProfileStatus !== 'verified') {
      const countryHint =
        state?.user_session?.collector_profile?.address_country ??
        state?.user_session?.gallery_address?.country ??
        state?.user_session?.user_location?.country ??
        state?.user_session?.user_country ??
        null;
      try {
        await ensurePaymentProfile({ countryHint });
      }
      catch (profileError) {
        if (profileError?.code === 'BIDDER_PROFILE_CANCELLED') {
          return null;
        }
        throw profileError;
      }
    }

    let resolvedPaymentMethodId = paymentMethodOverride ?? selectedPaymentMethodId ?? null;
    if (!resolvedPaymentMethodId) {
      try {
        const loadResult = await loadPaymentMethods({ force: true });
        const fallbackId =
          loadResult?.selectedPaymentMethodId ??
          loadResult?.defaultPaymentMethodId ??
          (Array.isArray(loadResult?.methods) ? loadResult.methods[0]?.id : null);
        resolvedPaymentMethodId = paymentMethodOverride ?? selectedPaymentMethodId ?? fallbackId ?? null;
      }
      catch (methodError) {
        console.warn('[ARTWORK_AUCTION][ContentSection] Unable to load payment methods', methodError);
      }
    }

    if (!resolvedPaymentMethodId) {
      const paymentError = new Error('Add a payment method before placing your bid.');
      paymentError.code = 'PAYMENT_METHOD_REQUIRED';
      throw paymentError;
    }

    const bidCurrency = artworkData?.artwork_about?.artwork_currency;
    const idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const bidderId = state?.user_session?._id ?? null;
    const bidderUsername = state?.user_session?.user_username ?? null;
    const bidderType = state?.user_session?.user_type ?? null;
    let bidderImageId = null;
    const userImage = state?.user_session?.user_image ?? null;
    if (userImage && typeof userImage === 'object' && userImage.image_id) {
      bidderImageId = userImage.image_id;
    } else if (typeof userImage === 'string' && userImage) {
      bidderImageId = userImage;
    } else if (state?.user_session?.profile_image?.image_id) {
      bidderImageId = state.user_session.profile_image.image_id;
    } else if (state?.user_session?.gallery_image?.image_id) {
      bidderImageId = state.user_session.gallery_image.image_id;
    } else if (state?.user_session?.artist_image?.image_id) {
      bidderImageId = state.user_session.artist_image.image_id;
    }

    const pendingMetadata = {
      idempotencyKey,
      amount: Number(amount),
      bidderId,
      bidderUsername,
      bidderType,
      bidderImageId,
      createdAt: Date.now(),
      paymentMethodId: resolvedPaymentMethodId,
    };
    pendingBidRef.current = pendingMetadata;
    recentBidRef.current = pendingMetadata;
    registerBidderDetails(pendingMetadata);
    markPendingBidWaiting();

    try {
      const prepareResult = await auctionService.prepareBidAuthorization({
        artworkId: artworkData._id,
        amount,
        currency: bidCurrency,
        placedFrom: 'platform',
        idempotencyKey,
        paymentMethodId: resolvedPaymentMethodId,
      });
      const { response: prepareResponse, data: prepareData } = prepareResult;

      if (!prepareResponse.ok) {
        pendingBidRef.current = null;
        recentBidRef.current = null;
        clearPendingTimers();
        setPendingBidState(null);
        const errorMessage = prepareData?.error_message || 'Unable to prepare the bid authorization.';
        const error = new Error(errorMessage);
        error.response = prepareResponse;
        error.data = prepareData;
        throw error;
      }

      if (prepareData?.bid && prepareData?.replayed) {
        confirmPendingBid();
        pendingBidRef.current = null;
        recentBidRef.current = null;
        return prepareData;
      }

      let authorization = prepareData?.payment_authorization ?? null;
      if (!authorization?.id) {
        pendingBidRef.current = null;
        recentBidRef.current = null;
        clearPendingTimers();
        setPendingBidState(null);
        throw new Error('Unable to prepare the payment authorization. Please try again.');
      }

      if (requiresPaymentAuthorization(authorization) && typeof handleAuthorizationChallenge === 'function') {
        const pendingBidPayload = buildPendingBidPayload({
          artworkId: artworkData._id,
          amount,
          currency: bidCurrency,
          idempotencyKey,
          paymentMethodId: resolvedPaymentMethodId,
        });

        const challengeResult = await handleAuthorizationChallenge({
          ...authorization,
          amount,
          currency: bidCurrency,
          ...(pendingBidPayload ? { pending_bid: pendingBidPayload } : {}),
        });

        if (pendingBidPayload?.idempotency_key) {
          if (pendingBidRef.current) {
            pendingBidRef.current.idempotencyKey = pendingBidPayload.idempotency_key;
          }
          if (recentBidRef.current) {
            recentBidRef.current.idempotencyKey = pendingBidPayload.idempotency_key;
          }
        }

        if (pendingBidPayload) {
          if (challengeResult?.pending_bid_authorization?.id) {
            authorization = challengeResult.pending_bid_authorization;
          }
          else if (requiresPaymentAuthorization(authorization)) {
            const challengeErrorMessage =
              challengeResult?.pending_bid_error?.message ??
              'Unable to verify your payment method for this bid. Please try again.';
            const challengeError = new Error(challengeErrorMessage);
            challengeError.code = challengeResult?.pending_bid_error?.code ?? 'PENDING_BID_AUTHORIZATION_FAILED';
            throw challengeError;
          }
        }
      }

      const { response, data } = await auctionService.placeBid({
        artworkId: artworkData._id,
        authorizationId: authorization.id,
        placedFrom: 'platform',
        idempotencyKey,
      });

      if (!response.ok) {
        pendingBidRef.current = null;
        recentBidRef.current = null;
        clearPendingTimers();
        setPendingBidState(null);
        const errorMessage = data?.error_message || 'Unable to place bid. Please try again.';
        const error = new Error(errorMessage);
        error.response = response;
        error.data = data;
        throw error;
      }

      confirmPendingBid();
      if (data?.payment_authorization && requiresPaymentAuthorization(data.payment_authorization) && typeof handleAuthorizationChallenge === 'function') {
        try {
          await handleAuthorizationChallenge({
            ...data.payment_authorization,
            amount,
            currency: bidCurrency,
          });
        }
        catch (authorizationError) {
          console.warn('[ARTWORK_AUCTION] Failed to confirm payment authorization', authorizationError);
        }
      }
      if (pendingBidRef.current && pendingBidRef.current.idempotencyKey === idempotencyKey) {
        pendingBidRef.current = null;
      }
      if (recentBidRef.current && recentBidRef.current.idempotencyKey === idempotencyKey) {
        recentBidRef.current.confirmedViaHttp = true;
      }

      return data;
    } catch (error) {
      pendingBidRef.current = null;
      recentBidRef.current = null;
      clearPendingTimers();
      setPendingBidState(null);
      if (error?.code === 'BIDDER_PROFILE_CANCELLED') {
        return null;
      }
      throw error;
    }
  }, [
    artworkData?._id,
    artworkData?.artwork_about?.artwork_currency,
    confirmPendingBid,
    clearPendingTimers,
    markPendingBidWaiting,
    registerBidderDetails,
    ensurePaymentProfile,
    handleAuthorizationChallenge,
    paymentProfileStatus,
    state?.user_session,
    isAuctionBiddingBlocked,
    isGalleryViewer,
    isArtworkCreatorViewer,
    isOwnArtwork,
    selectedPaymentMethodId,
    loadPaymentMethods,
  ]);

  if (currentView === 'auction') {
    displayContent = (
      <ArtworkAuctionDesktop
        artworkData={artworkData}
        onUnlockSoldPrice={onUnlockSoldPrice}
        soldPricePending={soldPricePending}
        onBack={handleBackToDetails}
        onPlaceBid={handlePlaceBid}
        isCountdownFrozen={isCountdownFrozen}
        serverTimeOffsetMs={serverTimeOffsetMs}
        pendingBidState={pendingBidState}
        showBidPanel={!isAuctionBiddingBlocked}
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelectPaymentMethod={setSelectedPaymentMethodId}
        onReloadPaymentMethods={refreshPaymentMethods}
        isPaymentMethodLoading={isLoadingPaymentMethods}
        paymentMethodsError={paymentMethodsError}
        hasPaymentMethods={hasPaymentMethods}
        onManagePaymentMethods={handleManagePaymentMethods}
      />
    );
  } else if (currentView === 'purchase') {
    displayContent = (
      <BuyDetailsDesktop
        artworkData={artworkData}
        onBack={handleBackToDetails}
        onFooterChange={handleFooterChange}
        registerCheckoutHandler={registerCheckoutHandler}
        typeOfPurchase={purchaseType}
        initialLimitedEditionSelection={initialLimitedEditionSelection}
        initialPurchaseState={initialPurchaseResumeState}
      />
    );
  } else if (selectedSocial) {
    displayContent = (
      <Socials selectedSocial={selectedSocial} closeSocial={handleCloseSocials} />
    );
  } else {
    displayContent = (
      <ArtworkMoreDetailsDesktop
        artworkData={artworkData}
        socialsData={socialsData}
        onOpenThoughts={handleOpenThoughts}
        onOpenRatings={handleOpenRatings}
        onOpenAskAi={handleOpenAskAi}
        onLikeUpdate={handleLikeUpdate}
        hasLimitedEditionAvailable={hasLimitedEditionAvailable}
        onOpenLimitedEdition={handleOpenLimitedEdition}
        onOpenAddToArtlist={handleOpenAddToArtlist}
      />
    );
  }

  // onButtonClick:
  // - Si es artwork de subasta: siempre pasamos handleFooterButtonClick (abre la pantalla de subasta).
  // - Si NO es subasta y la obra está vendida (resale flow), NO pasamos onButtonClick para dejar que el PurchaseFooter maneje Options/Make offer.
  // - En otros casos (compra estándar), pasamos handleFooterButtonClick.
  const footerOnClick = isAuctionArtwork
    ? handleFooterButtonClick
    : (ownershipInfo.isSold ? undefined : handleFooterButtonClick);

  return (
    <div
      className="artwork-view-content-section__desktop__container"
      onWheelCapture={handleContainerWheel}
    >
      <div className="artwork-view-content-section__desktop">
        <div
          ref={contentBodyRef}
          className={bodyClassName}
          data-artwork-slider-lock=""
          onWheelCapture={handleBodyWheel}
          onPointerDownCapture={(e) => e.stopPropagation()}
          onTouchStartCapture={(e) => e.stopPropagation()}
        >
          {displayContent}
        </div>

        {shouldShowFooter && (
          <PurchaseFooterDesktop
            amountValue={footerState.amountValue}
            amountLabel={currentView === 'purchase' ? tPurchaseFooter('total_label') : footerState.amountLabel}
            centerAmount={currentView === 'details'
              && ownershipInfo.isOriginalProduct
              && ownershipInfo.isSold
              && artworkData?.sold_price_access?.status !== 'unlocked'}
            buttonLabel={footerState.buttonLabel}
            disabled={footerState.disabled}
            loading={footerState.loading}
            auxiliaryContent={footerState.auxiliaryContent}
            offerPriceDetails={footerState.offerPriceDetails}
            label={footerState.label}
            variant={footerState.variant}
            showFreeShipping={
              currentView === 'details'
              && !isAuctionArtwork
              && !ownershipInfo.isSold
              && footerState.variant === 'default'
              && freeShippingAvailable
            }
            onButtonClick={footerOnClick}
            resaleContext={resaleContext}
          />
        )}
      </div>

      <SellInStorePopup
        open={sellInStoreOpen}
        close={() => setSellInStoreOpen(false)}
        artworkData={artworkData}
        onSuccess={(updated) => {
          if (updated) {
            setArtworkData(updated);
          }
        }}
      />
      <ActionRejectedPopup
        open={galleryRejection.open}
        close={galleryRejection.close}
        description={galleryRejection.description}
      />
      <AddToArtlist open={openAddToArtlist} close={handleCloseAddToArtlist} />
    </div>
  );
}
